import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star, Quote, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star 
        key={i}
        className={`w-4 h-4 md:w-5 md:h-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
      />
    ));
  };

  return (
    <div className="pt-20" data-testid="testimonials-page">
      <SEO 
        title="Client Testimonials"
        description="Read what our clients say about Trid Enterprise Solutions. Discover how we've helped enterprises transform their Oracle ecosystems with exceptional results."
        keywords="Oracle Testimonials, Client Reviews, ERP Implementation Reviews, Oracle Partner Reviews, Enterprise Feedback"
      />
      {/* Hero Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-deep-obsidian via-deep-obsidian/95 to-deep-obsidian/80" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Testimonials</span>
            <h1 className="heading-xl text-white mb-4 md:mb-6 font-heading">
              What Our Clients
              <span className="block text-oracle-red">Say About Us</span>
            </h1>
            <p className="text-slate-300 font-body text-base md:text-lg lg:text-xl leading-relaxed">
              Don't just take our word for it. Hear from the enterprise leaders who have transformed their organizations with our Oracle solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-light py-8 md:py-12 border-b border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 text-center">
            <div>
              <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-oracle-red">98%</div>
              <div className="font-body text-xs md:text-sm text-slate-600">Client Satisfaction</div>
            </div>
            <div>
              <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-oracle-red">100+</div>
              <div className="font-body text-xs md:text-sm text-slate-600">Projects Delivered</div>
            </div>
            <div>
              <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-oracle-red">10+</div>
              <div className="font-body text-xs md:text-sm text-slate-600">Years Experience</div>
            </div>
            <div>
              <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-oracle-red">4.9/5</div>
              <div className="font-body text-xs md:text-sm text-slate-600">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="testimonials-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4 md:p-8">
                    <div className="flex space-x-1 mb-4">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div key={j} className="w-4 h-4 md:w-5 md:h-5 bg-slate-200 rounded" />
                      ))}
                    </div>
                    <div className="h-20 md:h-24 bg-slate-200 rounded mb-6" />
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-200 rounded-full" />
                      <div>
                        <div className="h-4 bg-slate-200 rounded w-28 md:w-32 mb-2" />
                        <div className="h-3 bg-slate-200 rounded w-40 md:w-48" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <p className="font-body text-slate-600 text-base md:text-lg">
                No testimonials available yet.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {testimonials.map((testimonial, index) => (
                <Card 
                  key={testimonial.id}
                  className="card-enterprise relative overflow-hidden"
                  data-testid={`testimonial-card-${index}`}
                >
                  <CardContent className="p-4 md:p-6 lg:p-8">
                    {/* Quote Icon */}
                    <Quote className="absolute top-4 md:top-6 right-4 md:right-6 w-8 h-8 md:w-12 md:h-12 text-oracle-red/10" />
                    
                    {/* Stars */}
                    <div className="flex space-x-0.5 md:space-x-1 mb-4 md:mb-6">
                      {renderStars(testimonial.rating)}
                    </div>
                    
                    {/* Quote */}
                    <p className="font-body text-slate-700 text-sm md:text-base leading-relaxed mb-6 md:mb-8 relative z-10">
                      "{testimonial.quote}"
                    </p>
                    
                    {/* Author */}
                    <div>
                      <div className="font-heading text-base md:text-lg font-bold text-slate-900">
                        {testimonial.title}
                      </div>
                      <div className="font-body text-xs md:text-sm text-slate-600">
                        {testimonial.company}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Quote */}
      <section className="section-dark py-12 md:py-20 lg:py-32">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <Quote className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 text-oracle-red mx-auto mb-6 md:mb-8" />
            <p className="font-body text-lg md:text-xl lg:text-2xl xl:text-3xl text-white leading-relaxed mb-6 md:mb-8 italic px-2">
              "Working with Trid Enterprise Solutions has been a game-changer for our organization. Their deep Oracle expertise and commitment to our success made all the difference."
            </p>
            <div>
              <div className="font-heading text-lg md:text-xl font-bold text-white mb-1">
                Enterprise Client
              </div>
              <div className="font-body text-sm md:text-base text-slate-400">
                Leading Global Enterprise
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="testimonials-cta">
        <div className="container-custom text-center">
          <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Join Our Success Stories</span>
          <h2 className="heading-lg text-slate-900 mb-4 md:mb-6 font-heading max-w-2xl mx-auto">
            Ready to Transform Your Enterprise?
          </h2>
          <p className="text-slate-600 font-body text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-xl mx-auto">
            Schedule a consultation to discuss how we can help achieve your Oracle transformation goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link to="/contact" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                data-testid="testimonials-consultation-btn"
              >
                Schedule Consultation
                <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
              </Button>
            </Link>
            <Link to="/case-studies" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-slate-300 text-slate-700 hover:bg-slate-100 font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
              >
                View Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
